import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { BonuslySolid, Box, Image, Text, useColorModeValue, VStack } from '@bonusly/design-system';

import spotlightRippleBg from '../../../assets/images/spotlight-ripple-bg.png';
import { sanitize } from '../../../hooks/use-sanitize-rich-text';
import { EmployeeSpotlightSection } from '../../../schemas/spotlight';
import { RichTextDisplay } from '../../rich-text-editor/components/rich-text-display';
import { UserSpotlightCardShell } from './user-spotlight-card-shell';
import { getUserSpotlightSectionTypeKey, getUserSpotlightSectionTypeLabelFallback } from './user-spotlight-type-label';

type SpotlightCardEmbedProps = {
	section: EmployeeSpotlightSection;
	rounded?: boolean;
};

const processQuoteParagraphs = (html: string): string => {
	// Convert <p>> "..."</p> patterns to <blockquote>...</blockquote>
	return html.replace(/<p>\s*>\s*"([^"]+)"([^<]*)<\/p>/g, (match, quoteText, afterQuote) => {
		const cleanText = quoteText.trim();
		const afterText = afterQuote.trim();
		const fullText = afterText ? `${cleanText}${afterText}` : cleanText;
		return `<blockquote>${fullText}</blockquote>`;
	});
};

export const SpotlightCardEmbed: FC<SpotlightCardEmbedProps> = ({ section, rounded = true }) => {
	const { t, i18n } = useTranslation('spotlight');
	const tagBgColor = useColorModeValue('teal.100', 'teal.700');
	const tagTextColor = useColorModeValue('teal.700', 'teal.50');
	const quoteBgColor = useColorModeValue('teal.25', 'tealAlpha.200');
	const quoteTextColor = useColorModeValue('teal.700', 'teal.50');

	const processedHtml = processQuoteParagraphs(section.textHtml);
	const sanitizedHtml = sanitize(processedHtml);
	const introText = section.intro?.replace(/\.$/, '') || '';
	const typeKey = getUserSpotlightSectionTypeKey(section.type);
	const translationKey = `sectionTypes.${typeKey}`;
	const hasTranslation = i18n.exists(`spotlight:${translationKey}`);
	const chipLabel = hasTranslation ? t(translationKey) : getUserSpotlightSectionTypeLabelFallback(section.type);

	return (
		<Box position="relative" borderRadius={rounded ? '2xl' : 'none'} overflow="hidden" data-testid="spotlight-card-embed" data-section-id={section.id}>
			<Image
				src={spotlightRippleBg}
				alt=""
				aria-hidden="true"
				position="absolute"
				inset={0}
				w="100%"
				h="100%"
				objectFit="cover"
				pointerEvents="none"
				userSelect="none"
				draggable={false}
				borderRadius="inherit"
			/>

			<VStack position="relative" zIndex={2} spacing={2} p={{ base: 6, md: 8 }} align="stretch">
				<Box display="flex" justifyContent="center" pb={2}>
					<Box as="span" display="inline-flex" alignItems="center" gap={1} px={4} py={2} borderRadius="full" fontSize="md" bg={tagBgColor} color={tagTextColor}>
						<BonuslySolid size="md" />
						<Text as="span">Spotlight • {chipLabel}</Text>
					</Box>
				</Box>

				<Box maxW="600px" mx="auto" w="100%">
					<UserSpotlightCardShell innerPadding={{ base: 6, md: 8 }} constrainHeight={false}>
						<VStack spacing={6} align="stretch">
							<VStack spacing={2} align="stretch">
								<Text
									fontFamily="Inter, sans-serif"
									fontSize={{ base: 'xs', md: 'sm' }}
									fontWeight="medium"
									lineHeight="shorter"
									color="text.default"
									opacity={0.7}
									textTransform="uppercase"
									letterSpacing="wider"
								>
									{introText}
								</Text>
								<Text
									as="h2"
									fontFamily="'Plus Jakarta Sans', sans-serif"
									fontSize={{ base: 'xl', md: '2xl' }}
									fontWeight="bold"
									lineHeight="shorter"
									color="text.default"
									mt={0}
								>
									{section.title}
								</Text>
							</VStack>

							<Box
								fontSize={{ base: 'sm', md: 'md' }}
								lineHeight="tall"
								color="text.default"
								sx={{
									'& p': { mb: 3 },
									'& p:last-child': { mb: 0 },
									'& blockquote': {
										position: 'relative',
										p: '12px 16px',
										m: '8px 0',
										borderLeft: 'none',
										borderRadius: '12px',
										bg: quoteBgColor,
										fontSize: { base: 'sm', md: 'md' },
										lineHeight: 'tall',
										color: quoteTextColor,
										'& p': { mb: 0 },
									},
								}}
							>
								<RichTextDisplay>{sanitizedHtml}</RichTextDisplay>
							</Box>
						</VStack>
					</UserSpotlightCardShell>
				</Box>
			</VStack>
		</Box>
	);
};
